module Client.AlcoholAudit

open Elmish
open Fable.React
open Fable.React.Props
open Shared.Domain
open Shared.Domain.AlcoholAuditDomain
open Fable.Core.JsInterop
open Fetch
open Fable.Core
open Fable.Import

type Progress =
    | NotStarted
    | QueryGender
    | QueryDrinkFrequency
    | QueryHowManyInDay
    | QueryBingeFrequency
    | QueryUnableToStop
    | QueryFailedToDo
    | QueryNeededADrink
    | QueryRemorse
    | QueryUnableToRemember
    | QueryInjured
    | QueryFriendConcerned
    | Result

type Msg =
    | SetGender of Gender
    | SetDrinkFrequency of DrinkFrequency
    | SetHowManyInDay of int
    | SetBingeFrequency of Frequency
    | SetUnableToStop of Frequency
    | SetFailedToDo of Frequency
    | SetNeededADrink of Frequency
    | SetRemorse of Frequency
    | SetUnableToRemember of Frequency
    | SetInjured of Occurance
    | SetFriendConcerned of Occurance
    | FetchError of exn
    | NextStep
    | PrevStep
    | ContinueToReferral of AlcoholAuditAnswers * tenant: string
    | AlcoholTextLoaded of AlcoholWording

type Model = 
    { 
        Audit: AlcoholAuditAnswers
        Stage: Progress
        Score: int
        EnteredGender: Gender option
        Tenant: string
        AlcoholText: AlcoholWording
    }

let getAlcoholText tenant =
    let url = Shared.Urls.AlcoholText tenant
    FetchRequest.get url AlcoholTextLoaded FetchError

let stage =
    function
    | NotStarted -> 0
    | QueryGender -> 1
    | QueryDrinkFrequency -> 2
    | QueryHowManyInDay -> 3
    | QueryBingeFrequency -> 4
    | QueryUnableToStop -> 5
    | QueryFailedToDo -> 6
    | QueryNeededADrink -> 7
    | QueryRemorse -> 8
    | QueryUnableToRemember -> 9
    | QueryInjured -> 10
    | QueryFriendConcerned -> 11
    | Result -> 12

let nextStage score =
    function
    | Result -> None
    | NotStarted -> Some QueryGender
    | QueryGender ->  Some QueryDrinkFrequency
    | QueryDrinkFrequency ->  Some QueryHowManyInDay
    | QueryHowManyInDay -> Some QueryBingeFrequency
    | QueryBingeFrequency ->
        if score >= 5 then
            Some QueryUnableToStop
        else
            Some Result
    | QueryUnableToStop ->  Some QueryFailedToDo
    | QueryFailedToDo ->  Some QueryNeededADrink
    | QueryNeededADrink ->  Some QueryRemorse
    | QueryRemorse ->  Some QueryUnableToRemember
    | QueryUnableToRemember ->  Some QueryInjured
    | QueryInjured ->  Some QueryFriendConcerned
    | QueryFriendConcerned ->  Some Result

let prevStage score =
    function
    | Result ->
        if score >= 5 then
            Some QueryFriendConcerned
        else
            Some QueryBingeFrequency
    | NotStarted -> None
    | QueryGender ->  Some NotStarted
    | QueryDrinkFrequency ->  Some QueryGender
    | QueryHowManyInDay ->  Some QueryDrinkFrequency
    | QueryBingeFrequency ->  Some QueryHowManyInDay
    | QueryUnableToStop ->  Some QueryBingeFrequency
    | QueryFailedToDo ->  Some QueryUnableToStop
    | QueryNeededADrink ->  Some QueryFailedToDo
    | QueryRemorse ->  Some QueryNeededADrink
    | QueryUnableToRemember ->  Some QueryRemorse
    | QueryInjured ->  Some QueryUnableToRemember
    | QueryFriendConcerned ->  Some QueryInjured

let init tenant = 
    {
        Audit =
            {
                Gender = Male
                DrinkFrequency = DrinkFrequency.Never
                HowManyInDay = 0
                BingeFrequency = Never
                UnableToStop = Never
                FailedToDo = Never
                NeededADrink = Never
                Remorse = Never
                UnableToRemember = Never
                Injured = No
                FriendConcerned = No
            }
        Score = 0
        Stage = NotStarted
        EnteredGender = None
        Tenant = tenant
        AlcoholText =
            {
                LowRiskText = [||]
                ConditionText = [||]
                DependenceText = [||]
                ReferOnLow = false
                ReferOnIncreasing = false
                ReferOnHigh = false
                ReferOnDependence = false
            }
    }, getAlcoholText tenant

let update (msg:Msg) model : Model*Cmd<Msg>* string option = 
    match msg with
    | SetGender gender -> { model with EnteredGender = Some gender; Audit = { model.Audit with Gender = gender }; }, [], None
    | SetDrinkFrequency drinkFrequency -> { model with Audit = { model.Audit with DrinkFrequency = drinkFrequency }; }, [], None
    | SetHowManyInDay frequency -> { model with Audit = { model.Audit with HowManyInDay = frequency }; }, [], None
    | SetBingeFrequency frequency -> { model with Audit = { model.Audit with BingeFrequency = frequency }; }, [], None
    | SetUnableToStop frequency -> { model with Audit = { model.Audit with UnableToStop = frequency }; }, [], None
    | SetFailedToDo frequency -> { model with Audit = { model.Audit with FailedToDo = frequency }; }, [], None
    | SetNeededADrink frequency -> { model with Audit = { model.Audit with NeededADrink = frequency }; }, [], None
    | SetRemorse frequency -> { model with Audit = { model.Audit with Remorse = frequency }; }, [], None
    | SetUnableToRemember frequency -> { model with Audit = { model.Audit with UnableToRemember = frequency }; }, [], None
    | SetInjured occur -> { model with Audit = { model.Audit with Injured = occur }; }, [], None
    | SetFriendConcerned occur -> { model with Audit = { model.Audit with FriendConcerned = occur }; }, [], None
    | FetchError exn -> 
        Browser.Dom.console.error exn
        model, [], None
    | NextStep -> 
        let score = calculateAuditScore model.Audit
        let next =
            match nextStage score model.Stage with
            | None -> failwith "Invalid next stage"
            | Some stage -> stage
        { model with Score = score; Stage = next }, [], None
    | PrevStep ->
        let score = calculateAuditScore model.Audit
        let prev =
            match prevStage score model.Stage with
            | None -> failwith "Invalid prev stage"
            | Some stage -> stage
        { model with Stage = prev }, [], None
    | AlcoholTextLoaded num ->
        { model with AlcoholText = num }, [], None
    | ContinueToReferral _ -> model, Cmd.none, None

let nextButton hasNext disable first dispatch =
    [
        hr []
        div [ ClassName "centerflex col-sm-12" ] [            
            if hasNext then
                yield button [ 
                    ClassName "btn btn-lg btn-primary"
                    OnClick (fun _ -> dispatch NextStep)
                    Disabled disable
                    HTMLAttr.Type "button"
                    Style [                                                 
                        FlexGrow 2
                    ]
                ] [ str <| if first then "Begin" else "Continue" ]
            if not first then 
                yield button [ 
                    ClassName "btn btn-lg btn-default"
                    HTMLAttr.Type "button"
                    OnClick (fun _ -> dispatch PrevStep)
                    Style [
                        FlexGrow 1                        
                    ]
                ] [ str "Back" ]
        ]
    ]

let opt prop event dispatch v text =
    div [ ClassName "col-sm-12 blockoption" ] [
        button [
            ClassName ("btn btn-lg btn-block wrap " + (if prop = v then "btn-success" else "btn-default")) 
            OnClick (fun _ -> dispatch (event v))
        ] [
            str text
        ]
    ]

let renderNotStarted model dispatch =
    [
        yield div [ ClassName "title" ] [
            h2 [] [ str "Alcohol Audit"]                                
        ]
        yield p [ ClassName "alert alert-info" ] [
            str "This wizard is going to take you through the process of answering some questions about your use of alcoholic drinks during this past year."
        ]
        yield div [ ClassName "clearfix" ] [
            div [ 
                ClassName "col-sm-12" 
                Style [ 
                    BackgroundColor "white" 
                ] 
            ] [
                div [ ClassName "centerflex strip wrap" ] [
                    img [
                        Alt "beer bottle"
                        Src "images/beer bottle.png"
                    ]
                    img [
                        Alt "pint"
                        Src "images/pint.png"
                    ]
                    img [
                        Alt "spirits"
                        Src "images/spirits.png"
                    ]
                    img [
                        Alt "can"
                        Src "images/can.png"
                    ]
                    img [
                        Alt "cocktail"
                        Src "images/cocktail.png"
                    ]
                ]
            ]
            div [ 
                ClassName "col-sm-6 alcohol" 
            ] [
                p [] [
                    str "Alcoholic drinks contain alcohol (ethanol). They are available in various strengths, from beer, lager and alcopops (which are weakest), through to wine and eventually to spirits such as vodka and whisky."
                ]                                  
            ]
            div [ 
                ClassName "col-sm-6 alcohol" 
            ] [
                p [] [
                    str "Non-alcoholic (standard or soft) drinks do not contain alcohol."
                ]                                  
            ]
        ]
        yield! nextButton true false true dispatch
    ]

let renderGender model dispatch =
    [
        div [ ClassName "formstyle" ] [
            yield div [ ClassName "title" ] [
                h2 [] [ str "Gender"]                                
            ]
            yield p [ ClassName "alert alert-info" ] [
                str "Please choose which gender you identify as."
            ]
            yield div [ ClassName "col-sm-12 strip" ] [
                yield div [ ClassName "col-sm-6" ] [
                    button [
                        ClassName ("btn btn-lg btn-block " + match model.EnteredGender with Some Male -> "btn-success" | _ -> "btn-default") 
                        OnClick (fun _ -> dispatch <| SetGender Male)
                    ] [
                        str "Male"
                    ]
                ]
                yield div [ ClassName "col-sm-6" ] [
                    button [
                        ClassName ("btn btn-lg btn-block " + match model.EnteredGender with Some Female -> "btn-success" | _ -> "btn-default") 
                        OnClick (fun _ -> dispatch <| SetGender Female)
                    ] [
                        str "Female"
                    ]
                ]
            ]
            yield! nextButton true (model.EnteredGender.IsNone) false dispatch
        ]
    ]

let renderDrinkFrequency model dispatch =
    [
        div [ ClassName "formstyle" ] [
            yield div [ ClassName "title" ] [
                h2 [] [ str "How often do you have a drink containing alcohol?"]                                
            ]
            yield div [ ClassName "col-sm-12 strip" ] [
                let fopt = opt (model.Audit.DrinkFrequency) SetDrinkFrequency dispatch 

                yield fopt DrinkFrequency.Never "Never" 
                yield fopt DrinkFrequency.Monthly "Monthly or less" 
                yield fopt DrinkFrequency.MultiplePerMonth "2-4 times a month"
                yield fopt DrinkFrequency.FewTimesAWeek "2-3 times a week"
                yield fopt DrinkFrequency.MostDays "4 or more times a week"
                
            ]
            yield! nextButton true false false dispatch
        ]
    ]

let renderHowManyInDay model dispatch =
    [
        div [ ClassName "formstyle" ] [
            yield div [ ClassName "title" ] [
                h2 [] [ str "How many units of alcohol do you drink on a typical day when you are drinking?" ]                                
            ]
            yield div [ ClassName "col-sm-12 strip" ] [
                let fopt = opt (model.Audit.HowManyInDay) SetHowManyInDay dispatch 

                yield fopt -1 "N/A" 
                yield fopt 0 "1 to 2" 
                yield fopt 3 "3 to 4" 
                yield fopt 5 "5 to 6"
                yield fopt 7 "7 to 9"
                yield fopt 10 "10 or more"
                
            ]
            yield! nextButton true false false dispatch
        ]
    ]

let renderBingeFrequency model dispatch =
    [
        div [ ClassName "formstyle" ] [
            yield div [ ClassName "title" ] [
                h2 [] [ str <| sprintf "How often have you had %d units on a single occasion in the last year?" (match model.EnteredGender with Some Male -> 8 | Some Female -> 6 | _ -> 0) ]                                
            ]
            yield div [ ClassName "col-sm-12 strip" ] [
                let fopt = opt (model.Audit.BingeFrequency) SetBingeFrequency dispatch 

                yield fopt Frequency.Never "Never" 
                yield fopt Frequency.LessThanMonthly "Less than monthly" 
                yield fopt Frequency.Monthly "Monthly"
                yield fopt Frequency.Weekly "Weekly"
                yield fopt Frequency.AlmostDaily "Daily or almost daily"
                
            ]
            yield! nextButton true false false dispatch
        ]
    ]

let renderUnableToStop model dispatch =
    [
        div [ ClassName "formstyle" ] [
            yield div [ ClassName "title" ] [
                h2 [] [ str "How often during the last year have you found that you were not able to stop drinking once you had started?" ]                                
            ]
            yield div [ ClassName "col-sm-12 strip" ] [
                let fopt = opt (model.Audit.UnableToStop) SetUnableToStop dispatch 

                yield fopt Frequency.Never "Never" 
                yield fopt Frequency.LessThanMonthly "Less than monthly" 
                yield fopt Frequency.Monthly "Monthly"
                yield fopt Frequency.Weekly "Weekly"
                yield fopt Frequency.AlmostDaily "Daily or almost daily"
                
            ]
            yield! nextButton true false false dispatch
        ]
    ]

let renderFailedToDo model dispatch =
    [
        div [ ClassName "formstyle" ] [
            yield div [ ClassName "title" ] [
                h2 [] [ str "How often during the last year have you failed to do what was normally expected from you because of your drinking?" ]                                
            ]
            yield div [ ClassName "col-sm-12 strip" ] [
                let fopt = opt (model.Audit.FailedToDo) SetFailedToDo dispatch 

                yield fopt Frequency.Never "Never" 
                yield fopt Frequency.LessThanMonthly "Less than monthly" 
                yield fopt Frequency.Monthly "Monthly"
                yield fopt Frequency.Weekly "Weekly"
                yield fopt Frequency.AlmostDaily "Daily or almost daily"
                
            ]
            yield! nextButton true false false dispatch
        ]
    ]

let renderNeededADrink model dispatch =
    [
        div [ ClassName "formstyle" ] [
            yield div [ ClassName "title" ] [
                h2 [] [ str "How often during the last year have you needed an alcoholic drink in the morning to get yourself going after a heavy drinking session?" ]                                
            ]
            yield div [ ClassName "col-sm-12 strip" ] [
                let fopt = opt (model.Audit.NeededADrink) SetNeededADrink dispatch 

                yield fopt Frequency.Never "Never" 
                yield fopt Frequency.LessThanMonthly "Less than monthly" 
                yield fopt Frequency.Monthly "Monthly"
                yield fopt Frequency.Weekly "Weekly"
                yield fopt Frequency.AlmostDaily "Daily or almost daily"
                
            ]
            yield! nextButton true false false dispatch
        ]
    ]

let renderRemorse model dispatch =
    [
        div [ ClassName "formstyle" ] [
            yield div [ ClassName "title" ] [
                h2 [] [ str "How often during the last year have you had a feeling of guilt or remorse after drinking?" ]                                
            ]
            yield div [ ClassName "col-sm-12 strip" ] [
                let fopt = opt (model.Audit.Remorse) SetRemorse dispatch 

                yield fopt Frequency.Never "Never" 
                yield fopt Frequency.LessThanMonthly "Less than monthly" 
                yield fopt Frequency.Monthly "Monthly"
                yield fopt Frequency.Weekly "Weekly"
                yield fopt Frequency.AlmostDaily "Daily or almost daily"
                
            ]
            yield! nextButton true false false dispatch
        ]
    ]

let renderUnableToRemember model dispatch =
    [
        div [ ClassName "formstyle" ] [
            yield div [ ClassName "title" ] [
                h2 [] [ str "How often during the last year have you been unable to remember what happened the night before because you had been drinking?" ]                                
            ]
            yield div [ ClassName "col-sm-12 strip" ] [
                let fopt = opt (model.Audit.UnableToRemember) SetUnableToRemember dispatch 

                yield fopt Frequency.Never "Never" 
                yield fopt Frequency.LessThanMonthly "Less than monthly" 
                yield fopt Frequency.Monthly "Monthly"
                yield fopt Frequency.Weekly "Weekly"
                yield fopt Frequency.AlmostDaily "Daily or almost daily"
                
            ]
            yield! nextButton true false false dispatch
        ]
    ]

let renderInjured model dispatch =
    [
        div [ ClassName "formstyle" ] [
            yield div [ ClassName "title" ] [
                h2 [] [ str "Have you or somebody else been injured as a result of your drinking?" ]                                
            ]
            yield div [ ClassName "col-sm-12 strip" ] [
                let fopt = opt (model.Audit.Injured) SetInjured dispatch 

                yield fopt Occurance.No "No" 
                yield fopt Occurance.NotInLastYear "Yes, but not in the last year" 
                yield fopt Occurance.InLastYear "Yes, during the last year"                                
            ]
            yield! nextButton true false false dispatch
        ]
    ]

let renderFriendConcerned model dispatch =
    [
        div [ ClassName "formstyle" ] [
            yield div [ ClassName "title" ] [
                h2 [] [ str "Has a relative or friend, doctor or other health worker been concerned about your drinking or suggested that you cut down?" ]                                
            ]
            yield div [ ClassName "col-sm-12 strip" ] [
                let fopt = opt (model.Audit.FriendConcerned) SetFriendConcerned dispatch 

                yield fopt Occurance.No "No" 
                yield fopt Occurance.NotInLastYear "Yes, but not in the last year" 
                yield fopt Occurance.InLastYear "Yes, during the last year"                                
            ]
            yield! nextButton true false false dispatch
        ]
    ]

let referButton model dispatch =
    button [ 
        ClassName "btn btn-lg btn-primary"
        HTMLAttr.Type "button"
        OnClick (fun _ -> dispatch (ContinueToReferral (model.Audit, model.Tenant)))
    ] [ str "Continue to self referral" ]

let renderBlock lines =
    p [] [
        for line in lines do
            yield str line
            yield br []
    ]

let renderLowRisk model dispatch =
    [
        yield renderBlock model.AlcoholText.LowRiskText

        yield ul [ ClassName "list-group" ] [
            li [ ClassName "list-group-item" ] [ str "When bored or stressed have a workout instead of drinking" ]
            li [ ClassName "list-group-item" ] [ str "Avoid going to the pub after work" ]
            li [ ClassName "list-group-item" ] [ str "Plan activities and tasks at those times you would usually drink" ]
            li [ ClassName "list-group-item" ] [ str "When you do drink, set yourself a limit and stick to it" ]
            li [ ClassName "list-group-item" ] [ str "Have your first drink after starting to eat" ]
            li [ ClassName "list-group-item" ] [ str "Quench your thirst with alcohol-free drinks before and inbetween alcoholic drinks" ]
            li [ ClassName "list-group-item" ] [ str "Avoid drinking in rounds or in large groups" ]
            li [ ClassName "list-group-item" ] [ str "Switch to low alcohol beer/lager" ]
            li [ ClassName "list-group-item" ] [ str "Avoid or limit the time spent with \"heavy\" drinking friends" ]
        ]

        yield p [] [ str "If you want to refer yourself to our service for any help or advice then click the button below:" ]
        yield referButton model dispatch
    ]

let conditions conText = 
    [
        ul [ ClassName "list-group" ] [
            li [ ClassName "list-group-item" ] [ str "High blood pressure" ]
            li [ ClassName "list-group-item" ] [ str "Heart disease" ]
            li [ ClassName "list-group-item" ] [ str "Stroke" ]
            li [ ClassName "list-group-item" ] [ str "Liver disease" ]
            li [ ClassName "list-group-item" ] [ str "Cancer" ]
            li [ ClassName "list-group-item" ] [ str "Learning and memory problems" ]
            li [ ClassName "list-group-item" ] [ str "Mental health problems, including depression and anxiety" ]
            li [ ClassName "list-group-item" ] [ str "Social problems" ]
        ]
        renderBlock conText
    ]   

let renderIncreasingRisk model dispatch =
    [
        yield p [] [ str "Your alcohol audit suggests that you are drinking at increasing risk. Over time, excessive alcohol use can lead to the development of chronic diseases and other serious problems including:" ]
        yield! conditions model.AlcoholText.ConditionText
        yield p [] [ str "If you want to refer yourself to our service for any help or advice then click the button below:" ]
        yield referButton model dispatch
    ]

let renderHighRisk model dispatch =
    [
        yield p [] [
            str "Your alcohol audit score suggests that you are drinking at higher risk and could be dependent on alcohol. You may need help reducing or stopping your alcohol use."
            br []
            str "Drinking at this level can result in chronic diseases and other serious problems including:"
        ]
        yield! conditions model.AlcoholText.ConditionText
        yield p [] [ str "Please consider referring yourself to our service for some help by clicking the button below. Once you complete the form someone from the service will contact you to offer you an appointment." ]
        yield referButton model dispatch
    ]

let renderDependence model dispatch =
    [
        yield p [] [
            str "Your alcohol audit score suggests that you might be dependent on alcohol. You may need a medical alcohol detox or help reducing or stopping your alcohol use."
            br []
            str "Drinking at this level can result in chronic diseases and other serious problems including:"
        ]
        yield! conditions model.AlcoholText.ConditionText
        yield renderBlock model.AlcoholText.DependenceText           
        if model.AlcoholText.ReferOnDependence then
            yield referButton model dispatch
    ]

let renderResult model dispatch =
    [
        let alcBox imgs title opacity =
            div [
                ClassName "alcohol-result"
                Style [
                    Opacity (if opacity then 1.0 else 0.4)
                ]                                    
            ] [
                img [
                    Alt title
                    Src imgs
                    
                ]
                h3 [] [ str title ]
            ]

        yield div [ ClassName "formstyle alcohol-results" ] [
            h1 [] [ str <| sprintf "You scored %d" model.Score ]
            div [ ClassName "centerflex wrap strip result"; Style [ BackgroundColor "white" ] ] [
                alcBox "images/spirits.png" "Lower Risk" (model.Score <= 7)
                alcBox "images/cocktail.png" "Increasing Risk" (model.Score >= 8 && model.Score <= 15)
                alcBox "images/beer bottle.png" "Higher Risk" (model.Score >= 16 && model.Score <= 19)
                alcBox "images/pint.png" "Possible Dependence" (model.Score >= 20)     
            ]
            div [] [
                match model.Score with
                | x when x <= 7 -> yield! renderLowRisk model dispatch    
                | x when x <= 15 -> yield! renderIncreasingRisk model dispatch   
                | x when x <= 19 -> yield! renderHighRisk model dispatch       
                | _ -> yield! renderDependence model dispatch   
            ]
        ]
        yield! nextButton false false false dispatch
    ]

let view model dispatch =
    [
        div [ ClassName "container" ] [
            yield div [ 
                ClassName "centerflex" 
                Id "alcoholAudit"
            ] [
                yield div [ 
                    ClassName "formstyle"
                    Keyboard.onEnterConditional (model.EnteredGender.IsSome) NextStep dispatch
                    ] [
                    match model.Stage with
                    | NotStarted | Result -> ()
                    | x ->
                        yield div [ ClassName "progress" ] [
                            let perStage = 100. / float (stage Result)
                            let width = perStage * float (stage x)
                            let swidth = sprintf "%f%%" width
                            yield div [ 
                                ClassName "progress-bar"
                                Role "progressbar"
                                Style [
                                    Width swidth
                                ]
                            ] [
                                span [ ClassName "hidden-xs hidden-sm" ] [
                                    str <| sprintf "Step %d of %d." (stage x) (stage Result)
                                ]
                            ]
                        ]
                    match model.Stage with
                    | NotStarted ->  yield! renderNotStarted model dispatch        
                    | QueryGender -> yield! renderGender model dispatch
                    | QueryDrinkFrequency -> yield! renderDrinkFrequency model dispatch
                    | QueryHowManyInDay -> yield! renderHowManyInDay model dispatch   
                    | QueryBingeFrequency -> yield! renderBingeFrequency model dispatch       
                    | QueryUnableToStop -> yield! renderUnableToStop model dispatch   
                    | QueryFailedToDo -> yield! renderFailedToDo model dispatch       
                    | QueryNeededADrink -> yield! renderNeededADrink model dispatch
                    | QueryRemorse -> yield! renderRemorse model dispatch
                    | QueryUnableToRemember -> yield! renderUnableToRemember model dispatch
                    | QueryInjured -> yield! renderInjured model dispatch
                    | QueryFriendConcerned -> yield! renderFriendConcerned model dispatch
                    | Result -> yield! renderResult model dispatch
                ]
            ]
        ]
    ]
